import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import {
  ShieldAlert, ShieldCheck, Clock, AlertTriangle, Brain, RefreshCcw, CheckCircle2
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar
} from 'recharts';

const Kpi = ({ icon: Icon, label, value, suffix = '', color, tone }) => {
  const toneMap = {
    red: 'bg-red-50 border-red-100', emerald: 'bg-emerald-50 border-emerald-100',
    amber: 'bg-amber-50 border-amber-100', blue: 'bg-blue-50 border-blue-100', indigo: 'bg-indigo-50 border-indigo-100',
  };
  return (
    <Card className={`border ${toneMap[tone] || 'bg-slate-50 border-slate-200'}`}>
      <CardContent className="p-4">
        <Icon className="w-5 h-5" style={{ color }} />
        <p className="text-2xl font-bold mt-2" style={{ color, fontFamily: 'var(--font-heading)' }}>{value}{suffix}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </CardContent>
    </Card>
  );
};

const ExecutiveCommandCenter = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [gov, setGov] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [cc, ag] = await Promise.all([
        api.getCommandCenter(getAuthHeaders()),
        api.getAIGovernance(getAuthHeaders()),
      ]);
      setData(cc.data);
      setGov(ag.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load command center');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'ReasonDMS | Command Center';
    load();
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-slate-500" data-testid="executive-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" /> Loading command center…
      </div>
    );
  }
  if (!data) return null;

  const r = data.risk || {}, c = data.compliance || {}, w = data.workflows || {}, s = data.sla || {}, ai = data.ai_governance || {};

  return (
    <div data-testid="executive-command-center" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Executive Command Center</h1>
          <p className="text-sm text-slate-500 mt-0.5">Real-time KPIs, AI governance &amp; risk overview · auto-refreshes every 60s</p>
        </div>
        <Button data-testid="cc-refresh-btn" variant="outline" size="sm" onClick={() => load()}>
          <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Row 1: KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Kpi icon={ShieldAlert} label="Avg Risk Score" value={r.avg_risk_score ?? 0} color="#dc2626" tone="red" />
        <Kpi icon={ShieldCheck} label="Compliance Score" value={c.score ?? 0} suffix="%" color="#059669" tone="emerald" />
        <Kpi icon={Clock} label="Workflows Overdue" value={w.overdue ?? 0} color="#ea580c" tone="amber" />
        <Kpi icon={AlertTriangle} label="SLA At-Risk" value={s.at_risk ?? 0} color="#d97706" tone="amber" />
        <Kpi icon={Brain} label="AI Actions (30d)" value={ai.total_ai_actions_30d ?? 0} color="#7c3aed" tone="indigo" />
      </div>

      {/* Row 2: trend + bottleneck */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Risk trend (7 days)</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 220 }} data-testid="cc-risk-trend">
              <ResponsiveContainer>
                <LineChart data={r.risk_trend_7d || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip />
                  <Line type="monotone" dataKey="avg_risk" stroke="#dc2626" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Workflow bottlenecks {w.bottleneck_step ? `· slowest: ${w.bottleneck_step} (${w.bottleneck_avg_hours}h)` : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 220 }} data-testid="cc-bottleneck">
              <ResponsiveContainer>
                <BarChart data={w.step_averages || []} layout="vertical" margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" stroke="#94a3b8" fontSize={11} unit="h" />
                  <YAxis type="category" dataKey="step" stroke="#94a3b8" fontSize={10} width={110} />
                  <Tooltip />
                  <Bar dataKey="avg_hours" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: AI Governance Cockpit */}
      <Card data-testid="ai-governance-cockpit">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Brain className="w-4 h-4 text-purple-600" />AI Governance Cockpit</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Confidence distribution</p>
              <div style={{ width: '100%', height: 200 }} data-testid="cc-confidence-dist">
                <ResponsiveContainer>
                  <BarChart data={gov?.confidence_distribution || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="bucket" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Recent AI actions</p>
              <div className="max-h-52 overflow-y-auto space-y-1.5" data-testid="cc-ai-feed">
                {(gov?.recent_actions || []).slice(0, 20).map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-xs border-b border-slate-50 pb-1.5">
                    <div className="min-w-0">
                      <span className="font-medium text-slate-700">{a.action}</span>
                      <span className="text-slate-400"> · {a.document_title}</span>
                    </div>
                    <span className="text-slate-400 shrink-0 ml-2">{a.timestamp ? new Date(a.timestamp).toLocaleDateString() : ''}</span>
                  </div>
                ))}
                {(gov?.recent_actions || []).length === 0 && <p className="text-xs text-slate-400 py-4 text-center">No AI actions recorded</p>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Row 4: SLA at-risk summary */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">SLA overview</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-100" data-testid="sla-at-risk">
              <p className="text-2xl font-bold text-amber-600">{s.at_risk ?? 0}</p>
              <p className="text-xs text-slate-500 mt-1">At risk (next 24h)</p>
            </div>
            <div className="p-4 rounded-lg bg-red-50 border border-red-100" data-testid="sla-breached">
              <p className="text-2xl font-bold text-red-600">{s.breached_today ?? 0}</p>
              <p className="text-xs text-slate-500 mt-1">Breached today</p>
            </div>
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100" data-testid="sla-ontrack">
              <p className="text-2xl font-bold text-emerald-600 flex items-center justify-center gap-1"><CheckCircle2 className="w-5 h-5" />{s.on_track ?? 0}</p>
              <p className="text-xs text-slate-500 mt-1">On track</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExecutiveCommandCenter;
